import { useEffect, useRef } from 'react';

import { getCannedValueClosedReason, getIsCannedValueClosed } from '@aline/common';
import { Apollo } from 'src/gql/generated';

export function getCanToggleCannedFieldEdit(args: {
  assigneeStatus: Apollo.WorkflowRecipientStatus | null | undefined;
  hasValue: boolean;
  isAssignedToCurrentUser: boolean;
  isAutoOnly: boolean;
  wasSentForSignature: boolean;
}): boolean {
  return (
    args.wasSentForSignature &&
    args.isAssignedToCurrentUser &&
    !args.isAutoOnly &&
    !getIsCannedValueClosed(args.assigneeStatus) &&
    args.hasValue
  );
}

export function getCanEditCannedFieldForm(args: {
  assigneeStatus: Apollo.WorkflowRecipientStatus | null | undefined;
  isAssignedToCurrentUser: boolean;
  isAutoOnly: boolean;
  isEditingLocked: boolean;
  wasSentForSignature: boolean;
}): boolean {
  return (
    args.wasSentForSignature &&
    args.isAssignedToCurrentUser &&
    !args.isAutoOnly &&
    !getIsCannedValueClosed(args.assigneeStatus) &&
    !args.isEditingLocked
  );
}

export function getCannedFieldFormDisabledReason(args: {
  assigneeStatus: Apollo.WorkflowRecipientStatus | null | undefined;
  isAssignedToCurrentUser: boolean;
  isAutoOnly: boolean;
  isEditingLocked: boolean;
  variant: Apollo.CannedFieldVariant;
  wasSentForSignature: boolean;
}): string {
  const closedReason = getCannedValueClosedReason(args.assigneeStatus);
  if (closedReason) return closedReason;
  if (args.isAutoOnly) {
    if (args.variant === Apollo.CannedFieldVariant.DateSigned)
      return 'Auto-filled with the signing date';
    return "Auto-filled with the signer's email";
  }
  if (!args.wasSentForSignature) return 'Editable during signing only';
  if (!args.isAssignedToCurrentUser) return 'Assigned to another signer';
  if (args.isEditingLocked) return 'Click the pencil icon to edit';
  return '';
}

export function getCannedFieldFormDisplayValue(args: {
  canEdit: boolean;
  defaultValue: string | null;
  isAutoOnly: boolean;
  savedValue: string | null;
  typedValue: string;
}): string {
  if (args.isAutoOnly) return args.savedValue ?? args.defaultValue ?? '';
  if (args.canEdit) return args.typedValue;
  return args.savedValue ?? args.defaultValue ?? '';
}

/**
 * On the pencil-lock locked → unlocked transition, abort any pending debounced
 * write from a stale edit so the newly-opened editor doesn't race a queued
 * flush. The typed buffer itself is intentionally preserved — the signer clicks
 * pencil to amend the saved value, not to retype it from scratch.
 */
export function useCancelCannedWriteOnUnlock(args: {
  isEditingLocked: boolean;
  onCancel: () => void;
}): void {
  const { isEditingLocked, onCancel } = args;
  const prevLockedRef = useRef(isEditingLocked);
  useEffect(() => {
    if (prevLockedRef.current && !isEditingLocked) onCancel();
    prevLockedRef.current = isEditingLocked;
  }, [isEditingLocked, onCancel]);
}

export function getCannedFieldTypeDisplayString(variant: Apollo.CannedFieldVariant): string {
  switch (variant) {
    case Apollo.CannedFieldVariant.DateSigned:
      return 'Date';
    case Apollo.CannedFieldVariant.UserName:
    case Apollo.CannedFieldVariant.UserEmail:
    case Apollo.CannedFieldVariant.UserTitle:
    case Apollo.CannedFieldVariant.Address:
    case Apollo.CannedFieldVariant.Company:
      return 'Text';
  }
}
